/* eslint-disable no-alert */
import React, {useState, useEffect, useContext} from 'react';
import {navigate} from "@reach/router";

import JobListContext from '../../../context/jobListContext';
import filterHelper from "../../../../hooks/useFilterHelper";

import DropDown from '../../../../components/dropdown';
import SectionLoadingLine from '../../../../components/sectionLoadingLine';
import SummaryCard from '../jobSummaryCard';

import { computeSummaryData, plannedSummaryFromCounts, reactiveSummaryFromCounts } from "./helper";
import { PMCallSummary } from "../../../../functions/GetPMCallSummaryFunction";
import { ReactiveCallSummary } from "../../../../functions/GetReactiveCallSummaryFunction";
import { DropdownCtn, HeadingCtn, JobTypeBtn, JobTypeCtn, SummaryList, Empty, TitleCtn } from './index.style';


interface JobSummaryCardListProps {
  // The planned counts from the database (PORTAL-72). Absent or null: count the planned list in the browser.
  plannedSummary?: PMCallSummary | null;

  // The reactive counts from the database (PORTAL-199). Absent or null: count the reactive list in the browser.
  reactiveSummary?: ReactiveCallSummary | null;
}

const JobSummaryCardList: React.FC<JobSummaryCardListProps> = ({ plannedSummary = null, reactiveSummary = null }): React.ReactElement => {
  const { reactiveJobList, plannedJobList, updateReactiveFilters, filtersReactive, filtersPlanned, updatePlannedFilters } = useContext(JobListContext);

  const [timePeriod, setTimePeriod] = useState('thisMonth');
  const [viewReactive, setViewReactive] = useState(true);
  const [reactiveSummaryList, setReactiveSummaryList] = useState([]);
  const [plannedSummaryList, setPlannedSummaryList] = useState([]);

  const { filterByDate, getDatePeriod } = filterHelper();

  const updateSummaryData = (newDate: string) => {
   let newReactiveData = filterByDate(newDate, reactiveJobList, 'reactive');
   let newPlannedData = filterByDate(newDate,plannedJobList,'planned');

    // The database's counts are used whenever they are held, so the reactive cards do not wait for the live list.
    const reactiveCounts = reactiveSummary !== null ? reactiveSummary.counts[newDate] : undefined;

    if(reactiveCounts !== undefined) {
      let summaryData = reactiveSummaryFromCounts(reactiveCounts);
      setReactiveSummaryList(Object.keys(summaryData).map(status => <SummaryCard summary={summaryData[status]} onClick={() => handleClick(status, 'reactive',newDate)}/>))
    } else if(reactiveJobList.length > 0) {
      let summaryData = computeSummaryData(newReactiveData,'reactive');
      setReactiveSummaryList(Object.keys(summaryData).map(status => <SummaryCard summary={summaryData[status]} onClick={() => handleClick(status, 'reactive',newDate)}/>))
    }

    // The database's counts are used whenever they are held, so the planned cards do not wait for the whole list.
    const summaryCounts = plannedSummary !== null ? plannedSummary.counts[newDate] : undefined;

    if(summaryCounts !== undefined) {
      let summaryData = plannedSummaryFromCounts(summaryCounts);
      setPlannedSummaryList(Object.keys(summaryData).map(status => <SummaryCard summary={summaryData[status]} onClick={() => handleClick(summaryData[status].status,'planned',newDate)}/>))
    } else if(plannedJobList.length > 0) {
      let summaryData = computeSummaryData(newPlannedData, 'planned');
      setPlannedSummaryList(Object.keys(summaryData).map(status => <SummaryCard summary={summaryData[status]} onClick={() => handleClick(summaryData[status].status,'planned',newDate)}/>))
    }
  };

  const handleClick = (status: string, type: string,newDate: string) => {
    if(type === 'reactive') {
      navigate('/app/reactiveMaintenance');
      updateReactiveFilters({
        ...filtersReactive,
        statusFilters: status,
        dateFilter: getDatePeriod(newDate),
      })
    } else if(type === 'planned') {
      navigate('/app/plannedMaintenance');
      updatePlannedFilters({
        ...filtersPlanned,
        statusFilter: status,
        dateFilter: getDatePeriod(newDate),
      })
    }
  };

  useEffect(() => {
    updateSummaryData(timePeriod);
  }, [reactiveJobList, plannedJobList, plannedSummary, reactiveSummary]);

  // The cards for a view whose counts have not arrived: every card, count 0, drawn grey.
  const pendingCards = (type: string) => {
    const cards = computeSummaryData([], type);

    return Object.keys(cards).map(status => <SummaryCard summary={cards[status]} pending onClick={() => {}}/>);
  };

  const viewPending = viewReactive ? reactiveSummaryList.length === 0 : plannedSummaryList.length === 0;

  function handleChange(newValue: any) {
    updateSummaryData(newValue);
    setTimePeriod(newValue);
  }

  return (
    <React.Fragment>
      <HeadingCtn>
        <TitleCtn>
          <span>Reports</span>
          <JobTypeCtn role="tablist" aria-label="Report type">
            <JobTypeBtn
              type="button"
              role="tab"
              aria-selected={viewReactive}
              active={viewReactive}
              onClick={(e): void => {
                e.preventDefault();
                setViewReactive(true);
                updateSummaryData(timePeriod);
              }}
            >
              REACTIVE
            </JobTypeBtn>
            <JobTypeBtn
              type="button"
              role="tab"
              aria-selected={!viewReactive}
              active={!viewReactive}
              onClick={(e): void => {
                e.preventDefault();
                setViewReactive(false);
                updateSummaryData(timePeriod);
              }}
            >
              PLANNED
            </JobTypeBtn>
          </JobTypeCtn>
        </TitleCtn>
        <DropdownCtn>
          <span>Show results for</span>
          <div>
            <DropDown defaultVal={1} optionList="dateFilter" onChange={handleChange} />
          </div>
        </DropdownCtn>
      </HeadingCtn>
      <SectionLoadingLine active={viewPending} />
      <SummaryList>
        {/* The cards are always drawn. Until their counts arrive they show grey zeros, then the real numbers. */}
        {viewReactive && (reactiveSummaryList.length > 0 ? reactiveSummaryList : pendingCards('reactive'))}
        {!viewReactive && (plannedSummaryList.length > 0 ? plannedSummaryList : pendingCards('planned'))}
        <Empty />
        <Empty />
      </SummaryList>
    </React.Fragment>
  );
};

export default JobSummaryCardList;
